import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  ArrowRight,
  Download
} from 'lucide-react';

const ProgramsPage = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const programs = [
    {
      id: 'optimization',
      name: 'Optimization Program',
      description: 'Comprehensive health optimization with personalized protocols for peak performance.',
      image: 'https://customer-assets.emergentagent.com/job_care-connect-157/artifacts/d13vtcqt_PATFZmgClN63d0DDI4iEy.png',
      features: ['Personalized assessment', 'Custom protocols', 'Ongoing monitoring']
    },
    {
      id: 'weight_loss',
      name: 'Weight Loss',
      description: 'Medically supervised weight loss program tailored to your body and goals.',
      image: 'https://customer-assets.emergentagent.com/job_care-connect-157/artifacts/f06snpds_Y-xURBQ4CcunUebNZCLrn.png',
      features: ['Medical evaluation', 'Nutrition planning', 'Weekly check-ins']
    },
    {
      id: 'hormonal',
      name: 'Hormone Optimization',
      description: 'Balance your hormones for improved energy, vitality, and overall wellness.',
      image: 'https://customer-assets.emergentagent.com/job_care-connect-157/artifacts/7slqjh3k_A72gN1Ma4BZe6yO1wKZDD.png',
      features: ['Hormone testing', 'Bioidentical treatments', 'Lifestyle guidance']
    },
    {
      id: 'longevity',
      name: 'Longevity',
      description: 'Advanced protocols designed to optimize healthspan and promote healthy aging.',
      image: 'https://customer-assets.emergentagent.com/job_care-connect-157/artifacts/7x5n8ul9_jDMi0h2u6U9xU8DFQydkO.png',
      features: ['Advanced testing', 'Anti-aging protocols', 'Supplement optimization']
    },
    {
      id: 'mental_health',
      name: 'Mental Health',
      description: 'Comprehensive mental wellness support including therapy and medication management.',
      image: 'https://customer-assets.emergentagent.com/job_care-connect-157/artifacts/ex7wp8a3_-focZ9cGf3SoN8Qon2pDa.png',
      features: ['Therapy sessions', 'Medication management', 'Stress reduction']
    },
    {
      id: 'life_coaching',
      name: 'Life Coaching',
      description: 'Personal development coaching to help you achieve your life and health goals.',
      image: 'https://customer-assets.emergentagent.com/job_care-connect-157/artifacts/cyhvu17q_BifZLduCBh4THB8pMEDmU.png',
      features: ['Goal setting', 'Accountability', 'Mindset coaching']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] pt-20 pb-12" data-testid="programs-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-black text-white mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            Beyond Urgent Care
          </h1>
          <p className="text-white/90 text-xl font-black max-w-3xl mx-auto" style={{ fontFamily: 'Merriweather, serif' }}>
            Our comprehensive wellness programs are designed to optimize your health and help you achieve lasting transformation.
          </p>
        </div>

        {/* Programs Grid with Images */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {programs.map((program) => (
            <div 
              key={program.id}
              className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow"
            >
              <div className="aspect-video relative overflow-hidden">
                <img 
                  src={program.image}
                  alt={program.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/80 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h3 className="text-xl font-black" style={{ fontFamily: 'Merriweather, serif' }}>{program.name}</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-slate-700 font-black mb-4" style={{ fontFamily: 'Merriweather, serif' }}>{program.description}</p>
                
                <div className="space-y-2 mb-6">
                  {program.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-base font-black text-slate-800" style={{ fontFamily: 'Merriweather, serif' }}>{feature}</span>
                    </div>
                  ))}
                </div>

                <Link to="/register-program">
                  <button 
                    className="w-full text-white font-black py-3 rounded-lg transition-all"
                    style={{ 
                      background: 'linear-gradient(to right, #1E3A8A, #3B82F6)',
                      fontFamily: 'Merriweather, serif'
                    }}
                  >
                    Learn More & Register
                    <ArrowRight className="w-4 h-4 inline ml-2" />
                  </button>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <Link to="/register-program">
            <button 
              className="inline-block bg-white text-[#1E3A8A] text-xl font-black px-8 py-4 rounded-lg transition-all shadow-lg hover:bg-white/90"
              style={{ fontFamily: 'Merriweather, serif' }}
            >
              Register for a Program
              <ArrowRight className="w-5 h-5 inline ml-2" />
            </button>
          </Link>
          <a href="https://iwumm.com" target="_blank" rel="noopener noreferrer">
            <button 
              className="inline-block bg-white/20 border-2 border-white text-white text-xl font-black px-8 py-4 rounded-lg transition-all hover:bg-white/30"
              style={{ fontFamily: 'Merriweather, serif' }}
            >
              Visit IWUMM.com
            </button>
          </a>
        </div>

        {/* Ready to Get Started CTA */}
        <div className="bg-white/95 backdrop-blur-sm px-10 py-8 rounded-2xl shadow-xl max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-black text-[#1E3A8A] mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            Ready to Get Started?
          </h2>
          <p className="text-slate-700 text-xl mb-8 font-black" style={{ fontFamily: 'Merriweather, serif' }}>
            Download Spruce Health now or chat with our AI assistant to find the right care for you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://spruce.care/integratedwellness" target="_blank" rel="noopener noreferrer">
              <button 
                className="inline-flex items-center gap-2 text-white text-xl font-black px-10 py-4 rounded-lg transition-all shadow-lg"
                style={{ 
                  background: 'linear-gradient(to right, #DC2626, #B91C1C)',
                  fontFamily: 'Merriweather, serif'
                }}
              >
                <Download className="w-5 h-5" />
                Download Spruce Health App
              </button>
            </a>
            <a href="tel:2015353582">
              <button 
                className="inline-block text-[#1E3A8A] border-2 border-[#1E3A8A] text-xl font-black px-10 py-4 rounded-lg transition-all hover:bg-[#1E3A8A]/5"
                style={{ fontFamily: 'Merriweather, serif' }}
              >
                Call (201) 535-3582
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramsPage;
